// ─── StockSpace AI Pipeline ───────────────────────────────────────────────────
// Multi-agent orchestration: Gatekeeper → Quick/Deep → Critic → Validator
// All agents are isolated Gemini calls with single responsibilities.

const GEMINI_URL = () => {
  const key = process.env.REACT_APP_GEMINI_API_KEY;
  return `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${key}`;
};

// ── Core Gemini caller ────────────────────────────────────────────────────────
async function callGemini(prompt, maxTokens = 500) {
  let lastError;
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const res = await fetch(GEMINI_URL(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contents: [{ parts: [{ text: prompt }] }],
          generationConfig: {
            temperature: 0,
            maxOutputTokens: maxTokens,
            thinkingConfig: { thinkingBudget: 0 },
          },
        }),
      });
      if (!res.ok) {
        const body = await res.text();
        throw new Error(`Gemini ${res.status}: ${body}`);
      }
      const data = await res.json();
      const raw = data.candidates?.[0]?.content?.parts?.[0]?.text || '';

      // Try the first (non-greedy) JSON block first
      const match = raw.match(/\{[\s\S]*?\}/);
      if (!match) {
        console.warn('No JSON block in Gemini response, raw:', raw.substring(0, 200));
        throw new Error('No JSON in response');
      }
      try {
        return JSON.parse(match[0]);
      } catch (parseErr) {
        // Fall back: try every {...} substring greedily until one parses
        const allMatches = raw.match(/\{[\s\S]*\}/g) || [];
        for (const m of allMatches) {
          try { return JSON.parse(m); } catch { /* keep trying */ }
        }
        throw new Error(`JSON parse failed: ${raw.substring(0, 100)}`);
      }
    } catch (err) {
      lastError = err;
      if (attempt === 0) await new Promise(r => setTimeout(r, 1500));
    }
  }
  throw lastError;
}

// ── In-memory cache (survives the session, resets on page reload) ─────────────
const _cache = new Map();

function getCached(key) {
  const e = _cache.get(key);
  if (!e) return null;
  if (Date.now() > e.expiresAt) { _cache.delete(key); return null; }
  return e.value;
}

function setCache(key, value, ttlMs) {
  _cache.set(key, { value, expiresAt: Date.now() + ttlMs });
}

// ═══════════════════════════════════════════════════════════════════════════════
// AGENT 1 — GATEKEEPER
// Only classifies relevance. Has zero knowledge of execution.
// Security boundary: can't be injected into executing because it can't execute.
// ═══════════════════════════════════════════════════════════════════════════════
const GATEKEEPER_PROMPT = `You are a security gatekeeper for an inventory management system.
Your ONLY job: classify if a query is inventory-related.
You have zero knowledge of how queries are executed — this is intentional.

Respond ONLY with valid JSON, no markdown:
{
  "valid": true,
  "queryType": "activity_log_filter",
  "reason": "one sentence",
  "clarifyingQuestion": null
}

queryType options:
- "activity_log_filter" — queries about transaction history, who sold what, when
- "demand_forecast" — predictions, stockout, when will run out
- "inventory_recommendation" — what to order, discount, or restock
- "off_topic" — not inventory related

Rules:
- valid: true for stock, products, sales, transactions, inventory, orders, suppliers, write-offs, restocks
- valid: false for weather, math, cooking, politics, code, or anything not inventory
- Set clarifyingQuestion only if genuinely ambiguous about inventory intent`;

export async function runGatekeeper(query) {
  const key = `gate:${query.toLowerCase().trim()}`;
  const cached = getCached(key);
  if (cached) return { ...cached, fromCache: true };

  const prompt = `${GATEKEEPER_PROMPT}\n\nQuery: "${query}"`;
  const result = await callGemini(prompt, 300);
  setCache(key, result, 10 * 60 * 1000); // 10 min
  return { ...result, fromCache: false };
}

// ═══════════════════════════════════════════════════════════════════════════════
// AGENT 2Q — QUICK EXECUTOR (Tier 1)
// Single call: parse filters + return confidence score.
// Confidence drives whether result is applied silently, with warning, or rejected.
// ═══════════════════════════════════════════════════════════════════════════════
function quickExecutorPrompt(validTypes, validPresets, users, groups) {
  return `You are a filter parser for an inventory activity log.
Parse the query into filter parameters AND rate your confidence in the result.

Valid transaction types: ${validTypes}
Valid date presets: ${validPresets} (day=24h, week=7d, month=30d, year=365d, all=all time)
Known users in this org: ${users}
Known product groups in this org: ${groups}

CRITICAL: "search" field = product name substring ONLY — never a group or category name.

Respond ONLY with valid JSON, no markdown:
{
  "search": "",
  "filterTypes": [],
  "filterUser": "All",
  "filterGroups": [],
  "datePreset": "month",
  "sortKey": "timestamp",
  "sortDir": "desc",
  "confidence": "high",
  "reason": "why this confidence level in one sentence",
  "interpretation": "plain English max 12 words",
  "suggestDeepAnalysis": false
}

Confidence rules:
- high: clear transaction type + clear time range + unambiguous intent
- medium: ambiguous time, unknown user, or multiple interpretations possible
- low: complex conditions, external data needed, or very ambiguous intent

Group/category rules:
- Only put a value in filterGroups if it closely matches one of the "Known product groups" above.
- If the query names a group-like word that is NOT in the known groups list, do not guess — leave filterGroups empty and treat the word as a possible product name in "search" instead.
- Never invent a group that isn't in the known list.

Transaction rules:
- "sales"/"sold" → filterTypes: ["sale"]
- "write-offs"/"expired"/"damaged" → filterTypes: ["writeoff"]
- "restocks"/"received" → filterTypes: ["restock"]
- "returns" → filterTypes: ["return"]
- "adjustments" → filterTypes: ["adjustment"]
- "most"/"largest" with qty context → sortKey: "delta", sortDir: "desc"
- "last week" → datePreset: "week", "last month" → "month", "today"/"24h" → "day"
- "by [name]" → match filterUser against known users case-insensitively
- Product name in query → put in search field only if it's a SPECIFIC product name, and only if it doesn't match a known group

Examples:
Query: "write-offs in stationary last week" (known groups include "Stationary")
{"search":"","filterTypes":["writeoff"],"filterUser":"All","filterGroups":["Stationary"],"datePreset":"week","sortKey":"timestamp","sortDir":"desc","confidence":"high","reason":"clear type, known group, clear range","interpretation":"Write-offs in Stationary, last 7 days","suggestDeepAnalysis":false}

Query: "sales of red pen" (no group named "red pen" exists)
{"search":"red pen","filterTypes":["sale"],"filterUser":"All","filterGroups":[],"datePreset":"month","sortKey":"timestamp","sortDir":"desc","confidence":"high","reason":"specific product name, no matching group, default range","interpretation":"Sales of red pen","suggestDeepAnalysis":false}`;
}

export async function runQuickAnalysis(query, validTypes, validPresets, users, groups) {
  const key = `quick:${query.toLowerCase().trim()}`;
  const cached = getCached(key);
  if (cached) return { ...cached, tier: 'quick', fromCache: true };

  const prompt = `${quickExecutorPrompt(validTypes, validPresets, users, groups)}\n\nQuery: "${query}"`;
  const result = await callGemini(prompt, 400);
  const enriched = { ...result, tier: 'quick', fromCache: false };
  setCache(key, enriched, 5 * 60 * 1000); // 5 min
  return enriched;
}

// ═══════════════════════════════════════════════════════════════════════════════
// AGENTS 2D, 3A, 3B, 4 — DEEP PIPELINE (Tier 2.5)
// Coordinator → Filter Specialist → Critic loop → Validator
// onProgress(state) is called at each stage so the UI can show live progress.
// ═══════════════════════════════════════════════════════════════════════════════
const COORDINATOR_PROMPT = `You are an orchestration coordinator for an inventory AI system.
Given the user's query, decide which specialist tools are needed and in what order.
You decide this at runtime — it is NOT hardcoded anywhere.

Available tools:
- "parse_filters": extract activity log filters (types, user, date, sort)
- "compute_velocity": calculate sales rates per product
- "identify_slow_movers": find products not selling well
- "forecast_stockout": predict when products will run out
- "generate_recommendations": produce actionable business advice

Respond ONLY with valid JSON, no markdown:
{
  "plan": [
    { "tool": "parse_filters", "reason": "why this tool is needed" }
  ]
}

For activity log filter queries: plan should be just [parse_filters]
For forecast queries: [compute_velocity, forecast_stockout]
For recommendation queries: [compute_velocity, identify_slow_movers, generate_recommendations]`;

function filterSpecialistPrompt(validTypes, validPresets, users, groups, criticFeedback) {
  return `You are a specialist filter parser for an inventory activity log.
${criticFeedback ? `IMPORTANT — previous attempt was rejected. Critic feedback: "${criticFeedback}"\nAddress this specifically in your revised output.` : ''}

Valid transaction types: ${validTypes}
Valid date presets: ${validPresets}
Known users: ${users}
Known product groups in this org: ${groups}

CRITICAL: "search" = product name only. Only use a value in filterGroups if it matches one of the known groups above — never invent one. If a group-like term isn't in the known list, treat it as a possible product name instead.

Respond ONLY with valid JSON, no markdown:
{
  "search": "",
  "filterTypes": [],
  "filterUser": "All",
  "filterGroups": [],
  "datePreset": "month",
  "sortKey": "timestamp",
  "sortDir": "desc",
  "confidence": "high",
  "reason": "why this confidence level in one sentence",
  "interpretation": "plain English max 12 words",
  "suggestDeepAnalysis": false
}`;
}

const CRITIC_PROMPT = (query, validTypes, validPresets, users, groups) => `You are a critic reviewing a filter parser's output for an inventory log query.
Verify the output matches the user's intent precisely.

Original query: "${query}"
Valid types: ${validTypes}
Valid presets: ${validPresets}
Known users: ${users}
Known product groups: ${groups}

Common mistakes to check:
- Group/category name put in "search" field instead of filterGroups (or vice versa — a made-up group that isn't in the known list)
- Wrong transaction types selected
- Date range doesn't match what user asked
- User filter set when no user mentioned in query
- Sort direction wrong (most → desc, least → asc)

Respond ONLY with valid JSON, no markdown:
{
  "verdict": "approved",
  "feedback": null,
  "issues": [],
  "confidence": "high"
}

verdict must be "approved" or "needs_revision"
feedback must be specific and actionable if needs_revision, null if approved`;

const VALIDATOR_PROMPT = (query) => `You are a final validator for an inventory AI system.
Check if the assembled output makes logical sense for the user's query.

Original query: "${query}"

Respond ONLY with valid JSON, no markdown:
{
  "verdict": "approved",
  "warning": null,
  "confidence": "high"
}

verdict must be "approved" or "flagged"
warning should be a user-facing message if flagged, null if approved`;

export async function runDeepAnalysis(query, validTypes, validPresets, users, groups, onProgress) {
  const key = `deep:${query.toLowerCase().trim()}`;
  const cached = getCached(key);
  if (cached) return { ...cached, fromCache: true };

  const agentsRun = [];
  let apiCallCount = 0;

  // ── Coordinator ────────────────────────────────────────────────────────────
  onProgress({ stage: 'coordinator', status: 'running', message: 'Planning execution…' });
  let coordinator;
  try {
    coordinator = await callGemini(`${COORDINATOR_PROMPT}\n\nQuery: "${query}"`, 300);
    apiCallCount++;
    agentsRun.push('coordinator');
    const planSteps = coordinator.plan?.length || 1;
    onProgress({ stage: 'coordinator', status: 'done', message: `Plan ready — ${planSteps} step${planSteps !== 1 ? 's' : ''}` });
  } catch (err) {
    onProgress({ stage: 'coordinator', status: 'error', message: 'Coordinator failed, using default plan' });
    coordinator = { plan: [{ tool: 'parse_filters', reason: 'default' }] };
  }

  // ── Filter Specialist + Critic loop ───────────────────────────────────────
  let specialistResult = null;
  let criticFeedback = null;
  const MAX_REVISIONS = 2;

  for (let attempt = 0; attempt <= MAX_REVISIONS; attempt++) {
    const isRevision = attempt > 0;
    onProgress({
      stage: 'specialist',
      status: 'running',
      message: isRevision ? `Revising (attempt ${attempt + 1})…` : 'Extracting filters…'
    });

    try {
      const specialistInput = `${filterSpecialistPrompt(validTypes, validPresets, users, groups, criticFeedback)}\n\nQuery: "${query}"${
        isRevision && specialistResult ? `\n\nPrevious attempt: ${JSON.stringify(specialistResult)}` : ''
      }`;
      specialistResult = await callGemini(specialistInput, 400);
      apiCallCount++;
      agentsRun.push(isRevision ? `specialist-revision-${attempt}` : 'specialist');
      onProgress({
        stage: 'specialist',
        status: 'done',
        message: isRevision ? 'Revision complete' : 'Filters extracted'
      });
    } catch (err) {
      onProgress({ stage: 'specialist', status: 'error', message: 'Specialist failed' });
      break;
    }

    // Run Critic (not after last attempt)
    if (attempt < MAX_REVISIONS) {
      onProgress({ stage: 'critic', status: 'running', message: `Reviewing output…` });
      try {
        const criticInput = `${CRITIC_PROMPT(query, validTypes, validPresets, users, groups)}\n\nSpecialist output: ${JSON.stringify(specialistResult)}`;
        const critique = await callGemini(criticInput, 300);
        apiCallCount++;
        agentsRun.push('critic');

        if (critique.verdict === 'approved') {
          onProgress({ stage: 'critic', status: 'done', message: 'Output approved ✓' });
          break;
        }
        criticFeedback = critique.feedback;
        onProgress({ stage: 'critic', status: 'done', message: `Revision requested: ${critique.feedback?.substring(0, 60)}…` });
      } catch (err) {
        onProgress({ stage: 'critic', status: 'error', message: 'Critic failed, skipping review' });
        break;
      }
    } else {
      onProgress({ stage: 'critic', status: 'done', message: 'Max revisions reached — proceeding' });
    }
  }

  // ── Validator ──────────────────────────────────────────────────────────────
  onProgress({ stage: 'validator', status: 'running', message: 'Final validation…' });
  let validation = { verdict: 'approved', warning: null, confidence: 'medium' };
  try {
    const validatorInput = `${VALIDATOR_PROMPT(query)}\n\nFinal result: ${JSON.stringify(specialistResult)}`;
    validation = await callGemini(validatorInput, 200);
    apiCallCount++;
    agentsRun.push('validator');
    onProgress({
      stage: 'validator',
      status: validation.verdict === 'approved' ? 'done' : 'flagged',
      message: validation.verdict === 'approved' ? 'Validated ✓' : `Flagged: ${validation.warning}`
    });
  } catch (err) {
    onProgress({ stage: 'validator', status: 'error', message: 'Validator failed, proceeding without check' });
  }

  const result = {
    ...specialistResult,
    tier: 'deep',
    agentsRun,
    apiCallsUsed: apiCallCount,
    validation,
    warning: validation.verdict === 'flagged' ? validation.warning : null,
    fromCache: false,
  };

  setCache(key, result, 24 * 60 * 60 * 1000); // 24h — deep results are expensive
  return result;
}